import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { POST_LOGIN_PATH, POST_SIGNUP_PATH, desktopAppAuthUrl, safeAppPath } from "@prime-assistant/shared";
import { apiFetch, getApiBase } from "./api";
import {
  googleSignInUrl,
  isBrowserAuthHandoff,
  isDesktopShell,
  openInSystemBrowser,
} from "./desktop-shell";
import { clearStoredSessionToken, getStoredSessionToken, peekStoredSessionUser, setStoredSessionToken } from "./session-token";

export type AuthUser = { email: string; name: string };
type AuthConfig = { googleConfigured: boolean; demoAllowed: boolean };
type AuthContextValue = {
  user: AuthUser | null;
  status: "loading" | "authenticated" | "unauthenticated";
  googleConfigured: boolean;
  demoAllowed: boolean;
  refresh: () => Promise<void>;
  signInDemo: (input: { name?: string; email?: string; redirectTo?: string }) => Promise<string>;
  signInPassword: (input: { email: string; password: string; redirectTo?: string }) => Promise<string>;
  signUpPassword: (input: { email: string; password: string; name?: string; redirectTo?: string }) => Promise<string>;
  applySession: (input: { token: string; user: AuthUser; redirectTo?: string }) => void;
  startGoogleSignIn: (redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
};
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<"loading" | "authenticated" | "unauthenticated">("loading");
  const [config, setConfig] = useState<AuthConfig>({ googleConfigured: false, demoAllowed: false });

  const refresh = useCallback(async () => {
    try {
      setConfig(await apiFetch<AuthConfig>("/auth/config"));
    } catch {
      /* keep last known auth config */
    }
    try {
      const session = await apiFetch<{
        authenticated?: boolean;
        user?: { email?: string; name?: string } | null;
      }>("/auth/session");
      if (session.authenticated && session.user?.email) {
        const existing = getStoredSessionToken();
        if (existing) {
          setStoredSessionToken(existing);
        }
        setUser({
          email: session.user.email,
          name: session.user.name || session.user.email,
        });
        setStatus("authenticated");
        return;
      }
      if (session.authenticated === false) {
        clearStoredSessionToken();
        setUser(null);
        setStatus("unauthenticated");
        return;
      }
    } catch {
      const peeked = peekStoredSessionUser();
      if (peeked) {
        setUser(peeked);
        setStatus("authenticated");
        return;
      }
    }
    if (!getStoredSessionToken()) {
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const applySession = useCallback((input: { token: string; user: AuthUser; redirectTo?: string }) => {
    setStoredSessionToken(input.token);
    setUser(input.user);
    setStatus("authenticated");
    if (isBrowserAuthHandoff() && input.redirectTo) {
      window.location.replace(desktopAppAuthUrl(input.token, input.redirectTo));
    }
  }, []);

  const signInDemo = useCallback(async (input: { name?: string; email?: string; redirectTo?: string }) => {
    const res = await apiFetch<{ token: string; redirectTo?: string; user: AuthUser }>("/auth/demo", {
      method: "POST", body: JSON.stringify(input),
    });
    const next = safeAppPath(res.redirectTo || input.redirectTo);
    applySession({ ...res, redirectTo: next });
    return next;
  }, [applySession]);

  const signInPassword = useCallback(async (input: { email: string; password: string; redirectTo?: string }) => {
    const res = await apiFetch<{ token: string; redirectTo?: string; user: AuthUser }>("/auth/login", {
      method: "POST", body: JSON.stringify(input),
    });
    const next = safeAppPath(res.redirectTo || input.redirectTo);
    applySession({ ...res, redirectTo: next });
    return next;
  }, [applySession]);

  const signUpPassword = useCallback(async (input: { email: string; password: string; name?: string; redirectTo?: string }) => {
    const res = await apiFetch<{ token: string; redirectTo?: string; user: AuthUser }>("/auth/register", {
      method: "POST", body: JSON.stringify(input),
    });
    const next = safeAppPath(res.redirectTo || input.redirectTo, POST_SIGNUP_PATH);
    applySession({ ...res, redirectTo: next });
    return next;
  }, [applySession]);

  const startGoogleSignIn = useCallback(async (redirectTo = POST_LOGIN_PATH) => {
    const desktop = isDesktopShell() || isBrowserAuthHandoff();
    const url = googleSignInUrl(getApiBase(), redirectTo, desktop);
    if (isDesktopShell()) {
      const opened = await openInSystemBrowser(url);
      if (opened) {
        return;
      }
    }
    window.location.href = url;
  }, []);

  const signOut = useCallback(async () => {
    try { await apiFetch("/auth/logout", { method: "POST" }); } catch { /* ignore */ }
    clearStoredSessionToken();
    setUser(null);
    setStatus("unauthenticated");
    // Also end the shared Wannlabs account session (Ember, wannlabs.com, other apps): hop through
    // wannlabs.com/signout, which revokes it and only returns to allow-listed URLs, landing on Prime's signed-out home.
    if (!isDesktopShell() && window.location.protocol === "https:" && /(^|\.)(brandmatchgrowth|wannlabs)\.com$/i.test(window.location.hostname)) {
      try { sessionStorage.setItem("prime_wl_sso_tried", "1"); } catch { /* ignore */ }
      const home = `${window.location.origin}/?signedout=1`;
      window.location.replace(`https://wannlabs.com/signout/?next=${encodeURIComponent(home)}`);
    }
  }, []);

  const value = useMemo(() => ({
    user, status, googleConfigured: config.googleConfigured, demoAllowed: config.demoAllowed,
    refresh, signInDemo, signInPassword, signUpPassword, applySession, startGoogleSignIn, signOut,
  }), [user, status, config, refresh, signInDemo, signInPassword, signUpPassword, applySession, startGoogleSignIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
